// Paleta e metadados de nível de acesso. Fixos no código (decisão T25 de
// .claude/rbac/entrega-unica-plano.md §3: "cores sem estilo inline arbitrário,
// níveis com classes/constantes fixas"). São constantes de build, nunca valor
// vindo de input do usuário — mesma categoria de GROUP_PALETTE em
// frontend/src/components/board/GroupSection.jsx:35-40.
export const LEVELS = [
  { value: 'viewer', label: 'Leitor', color: '#9699a6' },
  { value: 'contributor', label: 'Colaborador', color: '#00c875' },
  { value: 'editor', label: 'Editor', color: '#579bfc' },
  { value: 'owner', label: 'Proprietário', color: '#a25ddc' },
  { value: 'deny', label: 'Sem acesso', color: '#e2445c' },
];

// Níveis atribuíveis como padrão de um grupo (nunca 'deny' — arquitetura-v2.md §1.3).
export const ASSIGNABLE_LEVELS = LEVELS.filter((l) => l.value !== 'deny');

export const LEVEL_MAP = Object.fromEntries(LEVELS.map((l) => [l.value, l]));

export const LEVEL_RANK = { deny: 0, viewer: 1, contributor: 2, editor: 3, owner: 4 };

export function levelLabel(level) {
  return LEVEL_MAP[level]?.label || (level ? String(level) : '—');
}

export function levelColor(level) {
  return LEVEL_MAP[level]?.color || '#9699a6';
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return { r: 150, g: 153, b: 166 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

// Estilo "fantasma" (herdado) de um nível: mesma cor do nível, só que em
// baixa opacidade e borda tracejada, nunca preenchido. A doc é explícita
// (arquitetura-v2.md §6.4) em que "cor sozinha não serve — ela já está
// ocupada codificando o nível": o contraste herdado/sobrescrito precisa vir
// do PREENCHIMENTO, não de trocar a cor por um cinza genérico.
export function ghostLevelStyle(level) {
  const { r, g, b } = hexToRgb(levelColor(level));
  return {
    color: `rgba(${r}, ${g}, ${b}, 0.75)`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.35)`,
    borderStyle: 'dashed',
    backgroundColor: 'transparent',
  };
}

// Mesma paleta de GroupSection.jsx:35-40, achatada — usada para colorir o
// badge de grupo de permissão na planilha, já que o contrato de GET /roles
// desta entrega não traz coluna `color` (diferente de arquitetura-v2.md §1.3).
export const ROLE_PALETTE = [
  '#037f4c', '#00c875', '#9cd326', '#cab641', '#ffcb00',
  '#784bd1', '#a25ddc', '#0086c0', '#579bfc', '#66ccff',
  '#bb3354', '#e2445c', '#ff158a', '#ff5ac4', '#ff642e',
  '#fdab3d', '#7f5347', '#c4c4c4', '#757575',
];

// Cor determinística por grupo (mesmo grupo => mesma cor sempre), sem
// depender de coluna no backend.
export function roleColor(role) {
  if (!role) return '#9699a6';
  const key = String(role.system_key || role.id || role.name || '');
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return ROLE_PALETTE[hash % ROLE_PALETTE.length];
}

// Proprietário: papel travado (rank 100, `locked` em arquitetura-v2.md §3.2).
// Aceita tanto `system_key` (contrato novo) quanto `locked` (contrato antigo,
// usado hoje em UsersPage/InviteModal) — janela de troca de backend descrita
// em entrega-unica-plano.md §1.3.
export function isOwnerRole(role) {
  if (!role) return false;
  return role.system_key === 'owner' || role.locked === true;
}

export function isRoleAssignable(role) {
  return role?.assignable !== false;
}

// Exclusivas do Proprietário (arquitetura-v2.md §3.2). A revisão de segurança
// fez POST/PUT /roles recusar com 403 essas duas em qualquer grupo que não
// seja o papel travado — o frontend nunca deve oferecê-las nem enviá-las
// para outro grupo.
export const OWNER_ONLY_CAPABILITIES = ['org.reset', 'org.transfer_ownership'];

export function isOwnerOnlyCapability(key) {
  return OWNER_ONLY_CAPABILITIES.includes(key);
}

// Remove as capabilities exclusivas do Proprietário de uma lista antes de
// mandar para a API — usado ao salvar ou duplicar um grupo que as tenha
// (ex.: dado legado de um grupo de sistema).
export function stripOwnerOnlyCapabilities(capabilities) {
  return (capabilities || []).filter((c) => !isOwnerOnlyCapability(c));
}
